import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Chip,
  Tooltip,
  CircularProgress,
  Fade,
  Zoom,
  useTheme
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as AIIcon,
  Person as UserIcon,
  Code as CodeIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useNotification } from '../contexts/NotificationContext';

const AIAssistant = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! I'm your AI Network Assistant. How can I help you today?",
      sender: 'ai',
      timestamp: new Date(),
      type: 'text'
    },
    {
      id: 2,
      text: "You can ask me to analyze network data, explain scan results, or help troubleshoot issues.",
      sender: 'ai',
      timestamp: new Date(),
      type: 'text'
    }
  ]);
  
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const theme = useTheme();
  const { showNotification } = useNotification();

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!input.trim()) return;

    // Add user message
    const userMessage = {
      id: messages.length + 1,
      text: input,
      sender: 'user',
      timestamp: new Date(),
      type: 'text'
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    try {
      // Simulate AI response (replace with actual API call)
      setTimeout(() => {
        const aiResponses = generateAIResponse(input);
        setMessages(prev => [...prev, ...aiResponses]);
        setIsTyping(false);
      }, 1000);
    } catch (error) {
      console.error('Error getting AI response:', error);
      showNotification('Failed to get AI response', 'error');
      setIsTyping(false);
    }
  };

  const generateAIResponse = (userInput) => {
    const lowerInput = userInput.toLowerCase().trim();
    const responses = [];
    const timestamp = new Date();
    
    // Greetings
    if (/^(hi|hello|hey|greetings|yo)/i.test(lowerInput)) {
      responses.push({
        id: messages.length + 2,
        text: "Hello! I'm your AI Network Assistant. I can help you with network diagnostics, security checks, and more. What would you like to do?",
        sender: 'ai',
        timestamp,
        type: 'text'
      });
    }
    // Network scan/analysis
    else if (/(scan|analy[sz]e|check|test)/i.test(lowerInput) && /(network|port|ip|dns)/i.test(lowerInput)) {
      responses.push({
        id: messages.length + 2,
        text: "I can help analyze network data. Here are some common tasks I can assist with:",
        sender: 'ai',
        timestamp,
        type: 'text'
      });
      
      responses.push({
        id: messages.length + 3,
        text: "quick_actions",
        sender: 'ai',
        timestamp,
        type: 'quick_actions',
        actions: [
          { text: 'Common Ports', command: 'show common ports' },
          { text: 'Network Status', command: 'check network status' },
          { text: 'DNS Lookup', command: 'dns lookup example.com' }
        ]
      });
    }
    // Security related
    else if (/(secure|security|vulnerability|firewall|ssl|tls)/i.test(lowerInput)) {
      responses.push({
        id: messages.length + 2,
        text: "For security-related tasks, I can help you check SSL certificates, analyze potential vulnerabilities, or explain security concepts. What specifically would you like to know?",
        sender: 'ai',
        timestamp,
        type: 'text'
      });
    }
    // Performance issues
    else if (/(slow|speed|performance|latency|bandwidth)/i.test(lowerInput)) {
      responses.push({
        id: messages.length + 2,
        text: "For performance issues, you might want to run a Speed Test or check for network congestion. Would you like me to help you with that?",
        sender: 'ai',
        timestamp,
        type: 'text'
      });
    }
    // Help/commands
    else if (/(help|what can you do|commands|support)/i.test(lowerInput)) {
      responses.push({
        id: messages.length + 2,
        text: "I can help you with various network tasks. Here are some examples:\n\n• 'Scan my network for open ports'\n• 'Check SSL certificate for example.com'\n• 'How do I improve my network security?'\n• 'Explain what a DNS lookup does'\n\nWhat would you like to know?",
        sender: 'ai',
        timestamp,
        type: 'text'
      });
    }
    // Default response for unrecognized input
    else {
      responses.push({
        id: messages.length + 2,
        text: "I'm here to help with network-related tasks. You can ask me to analyze scans, explain networking concepts, or help troubleshoot issues. Try asking something like 'How do I check open ports?' or 'What's my public IP?'",
        sender: 'ai',
        timestamp,
        type: 'text',
        actions: [
          { text: 'Show Help', command: 'help' },
          { text: 'Network Tools', command: 'show network tools' }
        ]
      });
    }
    
    return responses;
  };

  const handleQuickAction = (command) => {
    setInput(command);
    // Auto-send the command
    setTimeout(() => {
      const sendButton = document.querySelector('#send-message-button');
      if (sendButton) sendButton.click();
    }, 100);
  };

  const renderMessageContent = (message) => {
    switch (message.type) {
      case 'text':
        return (
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {message.text}
          </Typography>
        );
      
      case 'quick_actions':
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {message.actions?.map((action, index) => (
              <Chip
                key={index}
                label={action.text}
                onClick={() => handleQuickAction(action.command)}
                variant="outlined"
                size="small"
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        height: '600px',
        maxWidth: '800px',
        margin: '0 auto',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        color: 'text.primary',
        '& *': {
          color: 'text.primary !important',
        },
        border: `1px solid ${theme.palette.divider}`
      }}
    >
      {/* Header */}
      <Box 
        sx={{ 
          bgcolor: theme.palette.primary.main, 
          color: theme.palette.primary.contrastText,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <AIIcon />
        <Typography variant="h6">AI Network Assistant</Typography>
        <Chip 
          label="Beta" 
          size="small" 
          sx={{ 
            ml: 'auto',
            bgcolor: theme.palette.success.main,
            color: theme.palette.success.contrastText
          }} 
        />
      </Box>

      {/* Messages */}
      <Box 
        ref={messagesEndRef}
        sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          '& *': {
            color: 'text.primary !important',
          },
        }}
      >
        <List sx={{ width: '100%' }}>
          {messages.map((message, index) => (
            <React.Fragment key={message.id}>
              <ListItem 
                alignItems="flex-start"
                sx={{
                  flexDirection: message.sender === 'ai' ? 'row' : 'row-reverse',
                  textAlign: message.sender === 'ai' ? 'left' : 'right',
                  px: 1
                }}
              >
                <ListItemAvatar>
                  <Zoom in={true}>
                    <Avatar 
                      sx={{
                        bgcolor: message.sender === 'ai' 
                          ? theme.palette.primary.main 
                          : theme.palette.secondary.main
                      }}
                    >
                      {message.sender === 'ai' ? <AIIcon /> : <UserIcon />}
                    </Avatar>
                  </Zoom>
                </ListItemAvatar>
                <Box
                  sx={{
                    maxWidth: '80%',
                    ml: message.sender === 'ai' ? 1 : 0,
                    mr: message.sender === 'user' ? 1 : 0,
                    textAlign: 'left'
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: message.sender === 'ai' 
                        ? theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100]
                        : theme.palette.primary.main,
                      color: message.sender === 'ai'
                        ? theme.palette.getContrastText(theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100])
                        : theme.palette.primary.contrastText,
                      border: `1px solid ${theme.palette.divider}`,
                      display: 'inline-block',
                      maxWidth: '100%',
                      overflowWrap: 'break-word',
                      wordWrap: 'break-word',
                      wordBreak: 'break-word',
                      '& *': {
                        color: 'inherit !important',
                      }
                    }}
                  >
                    {renderMessageContent(message)}
                  </Paper>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block',
                      mt: 0.5,
                      color: theme.palette.text.secondary,
                      textAlign: 'right'
                    }}
                  >
                    {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              </ListItem>
              {index < messages.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          ))}
          {isTyping && (
            <ListItem>
              <ListItemAvatar>
                <AIIcon color="primary" />
              </ListItemAvatar>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="textSecondary">
                  Thinking...
                </Typography>
              </Box>
            </ListItem>
          )}
          <div ref={messagesEndRef} />
        </List>
      </Box>

      {/* Input */}
      <Box 
        sx={{ 
          p: 2, 
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSendMessage();
          }}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Ask me anything about networks, security, or your tools..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              multiline
              maxRows={4}
              disabled={isTyping}
            />
            <Tooltip title="Send message">
              <span>
                <IconButton 
                  color="primary" 
                  onClick={handleSendMessage}
                  disabled={!input.trim() || isTyping}
                  id="send-message-button"
                  type="submit"
                >
                  <SendIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </form>
        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            icon={<CodeIcon />}
            label="Analyze scan"
            size="small"
            variant="outlined"
            onClick={() => handleQuickAction('Analyze this port scan:')}
            sx={{ cursor: 'pointer' }}
          />
          <Chip
            icon={<SecurityIcon />}
            label="Security check"
            size="small"
            variant="outlined"
            onClick={() => handleQuickAction('Check security for:')}
            sx={{ cursor: 'pointer' }}
          />
          <Chip
            icon={<SpeedIcon />}
            label="Optimize network"
            size="small"
            variant="outlined"
            onClick={() => handleQuickAction('How can I optimize my network?')}
            sx={{ cursor: 'pointer' }}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default AIAssistant;
